import React, { useState, useEffect, useRef } from 'react';
import './FindCreatorsPanel.css';
import CreatorFilters from './CreatorFilters';
import CreatorGrid from './CreatorGrid';
import CreatorModal from './CreatorModal';
import AssignCreatorModal from './AssignCreatorModal';
import ShortlistCreatorModal from './ShortlistCreatorModal';
import FirestoreErrorBoundary from '../Components/FirestoreErrorBoundary';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { useSimpleCreators } from '../hooks/useSimpleCreators';

function FindCreatorsPanel({ onClose, isModal, context, selectedCreator }) {
  const [activeTab, setActiveTab] = useState('explore');
  const [activeSubFilter, setActiveSubFilter] = useState('all');
  const [filters, setFilters] = useState({
    gender: 'any',
    niche: 'any',
    age: 'any',
    hobbies: 'any'
  });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [preselectCampaign, setPreselectCampaign] = useState('');
  const [preselectBrief, setPreselectBrief] = useState(null);
  const [preselectScript, setPreselectScript] = useState(null);
  const [shortlistCampaign, setShortlistCampaign] = useState('');
  const [userCampaigns, setUserCampaigns] = useState([]);
  const [shortlistedCreators, setShortlistedCreators] = useState([]);
  const [favouriteCreators, setFavouriteCreators] = useState([]);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [selectedCreatorForModal, setSelectedCreatorForModal] = useState(null);
  const [showShortlistModal, setShowShortlistModal] = useState(false);
  const [creatorToShortlist, setCreatorToShortlist] = useState(null);
  const gridScrollAreaRef = useRef(null);

  // Use simple creator hook
  const { creators, loading, error, refresh } = useSimpleCreators();
  const [selectedCreators, setSelectedCreators] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [creatorsPerPage, setCreatorsPerPage] = useState(16); // 5 rows × ~3 columns (default)
  
  // Calculate creators per page based on screen size (5 rows)
  useEffect(() => {
    const updateCreatorsPerPage = () => {
      const width = window.innerWidth;
      if (width <= 700) {
        setCreatorsPerPage(5); // 5 rows × 1 column
      } else if (width <= 1200) {
        setCreatorsPerPage(10); // 5 rows × 2 columns
      } else {
        setCreatorsPerPage(16); // 5 rows × ~3 columns (default)
      }
    };
    
    updateCreatorsPerPage();
    window.addEventListener('resize', updateCreatorsPerPage);
    return () => window.removeEventListener('resize', updateCreatorsPerPage);
  }, []);
  
  // Reset to page 1 when filters or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeTab, activeSubFilter]);
  
  // Scroll to top when page changes
  useEffect(() => {
    if (gridScrollAreaRef.current) {
      gridScrollAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // Handle selectedCreator prop - open creator modal if provided
  useEffect(() => {
    if (selectedCreator) {
      setSelectedCreatorForModal(selectedCreator);
      setShowCreatorModal(true);
    }
  }, [selectedCreator]);

  // Simple creator selection functions
  const isSelected = (creatorId) => {
    return selectedCreators.some(c => c.id === creatorId);
  };

  const selectCreator = (creator) => {
    setSelectedCreators(prev => [...prev, creator]);
  };

  const deselectCreator = (creator) => {
    setSelectedCreators(prev => prev.filter(c => c.id !== creator.id));
  };

  const toggleCreatorSelection = (creator) => {
    if (isSelected(creator.id)) {
      deselectCreator(creator);
    } else {
      selectCreator(creator);
    }
  };

  const clearSelection = () => {
    setSelectedCreators([]);
  };

  const tabs = [
    { key: 'explore', label: 'Explore' },
    { key: 'recent', label: 'Recent' },
    { key: 'shortlist', label: 'Shortlist' },
    { key: 'favourite', label: 'Favourite' },
  ];

  // Get current creators based on active tab
  const getCurrentCreators = () => {
    let currentCreators = [];

    // Use real creators from creator dashboard
    const availableCreators = creators || [];
    

    switch (activeTab) {
      case 'explore':
        // Show all visible creators from creator dashboard
        currentCreators = availableCreators;
        break;
      case 'recent':
        // Show recently viewed creators (for now, just show all creators sorted by newest)
        currentCreators = availableCreators.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        break;
      case 'shortlist':
        // Show shortlisted creators for the selected campaign
        currentCreators = shortlistedCreators;
        break;
      case 'favourite':
        // Show favourite creators
        currentCreators = favouriteCreators;
        break;
      default:
        currentCreators = availableCreators;
    }

    // Apply sub-filtering based on activeSubFilter
    if (activeSubFilter && activeSubFilter !== 'all') {
      switch (activeSubFilter) {
        case 'rising':
          currentCreators = currentCreators.filter(creator => 
            creator.creatorLevel === 'rising_star' || creator.tier === 'Rising Star'
          );
          break;
        case 'pro':
          currentCreators = currentCreators.filter(creator => 
            creator.creatorLevel === 'pro_creator' || creator.tier === 'Pro'
          );
          break;
        case 'couples':
          currentCreators = currentCreators.filter(creator => 
            creator.gender === 'couple' || creator.name?.toLowerCase().includes('couple')
          );
          break;
        default:
          // No additional filtering for unknown sub-filters
          break;
      }
    }

    // Apply filters (Gender, Niche, Age, Hobbies)
    if (filters.gender && filters.gender !== 'any') {
      currentCreators = currentCreators.filter(creator => {
        const creatorGender = (creator.gender || '').toLowerCase();
        return creatorGender === filters.gender.toLowerCase();
      });
    }

    if (filters.niche && filters.niche !== 'any') {
      currentCreators = currentCreators.filter(creator => {
        const niches = creator.niches || creator.niche || [];
        const nicheArray = Array.isArray(niches) ? niches : (niches ? [niches] : []);
        return nicheArray.some(n => {
          if (!n) return false;
          const nicheStr = typeof n === 'string' ? n.toLowerCase() : String(n).toLowerCase();
          const filterNiche = filters.niche.toLowerCase();
          // Check for exact match or contains match
          return nicheStr === filterNiche || nicheStr.includes(filterNiche) || filterNiche.includes(nicheStr);
        });
      });
    }

    if (filters.age && filters.age !== 'any') {
      currentCreators = currentCreators.filter(creator => {
        const creatorAge = creator.age || creator.ageRange;
        if (!creatorAge) return false;
        
        // Handle age range filter (e.g., "18-24" or "55+")
        if (filters.age.includes('-')) {
          const [min, max] = filters.age.split('-').map(Number);
          if (typeof creatorAge === 'number') {
            return creatorAge >= min && creatorAge <= max;
          } else if (typeof creatorAge === 'string') {
            if (creatorAge.includes('-')) {
              // Creator has range like "18-24"
              const [creatorMin, creatorMax] = creatorAge.split('-').map(Number);
              return (creatorMin >= min && creatorMin <= max) || (creatorMax >= min && creatorMax <= max);
            } else {
              const ageNum = parseInt(creatorAge);
              if (!isNaN(ageNum)) {
                return ageNum >= min && ageNum <= max;
              }
            }
          }
        } else if (filters.age.endsWith('+')) {
          // Handle "55+" range
          const minAge = parseInt(filters.age.replace('+', ''));
          if (typeof creatorAge === 'number') {
            return creatorAge >= minAge;
          } else if (typeof creatorAge === 'string') {
            const ageNum = parseInt(creatorAge);
            if (!isNaN(ageNum)) {
              return ageNum >= minAge;
            }
          }
        }
        return false;
      });
    }

    if (filters.hobbies && filters.hobbies !== 'any') {
      currentCreators = currentCreators.filter(creator => {
        const hobbies = creator.hobbies || [];
        const hobbiesArray = Array.isArray(hobbies) ? hobbies : [hobbies];
        return hobbiesArray.some(h => 
          h && h.toLowerCase().includes(filters.hobbies.toLowerCase())
        );
      });
    }

    return currentCreators;
  };
  
  // Pagination logic
  const getCurrentCreatorsPaginated = () => {
    const allCreators = getCurrentCreators();
    const startIndex = (currentPage - 1) * creatorsPerPage;
    const endIndex = startIndex + creatorsPerPage;
    return {
      paginatedCreators: allCreators.slice(startIndex, endIndex),
      totalPages: Math.ceil(allCreators.length / creatorsPerPage),
      totalCreators: allCreators.length
    };
  };
  
  const { paginatedCreators, totalPages, totalCreators } = getCurrentCreatorsPaginated();

  // On mount, if context is provided, preselect campaign/brief/script or open creator modal
  useEffect(() => {
    if (context) {
      setPreselectCampaign(context.campaign || '');
      setPreselectBrief(context.brief || null);
      setPreselectScript(context.script || null);
      
      // Auto-open creator modal if openCreatorId is provided
      if (context.openCreatorId && creators && creators.length > 0) {
        const creatorToOpen = creators.find(c => c.id === context.openCreatorId);
        if (creatorToOpen) {
          setSelectedCreatorForModal(creatorToOpen);
          setShowCreatorModal(true);
        }
      }
    }
  }, [context, creators]);

  // No need for complex initialization - the hook handles fetching creators

  // Fetch user campaigns on mount
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
    const unsubscribe = onSnapshot(campaignsRef, (snapshot) => {
      const fetchedCampaigns = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUserCampaigns(fetchedCampaigns);
      // Set the most recent campaign as default if none is selected
      if (fetchedCampaigns.length > 0 && !shortlistCampaign) {
        setShortlistCampaign(fetchedCampaigns[0].id);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch shortlisted creators for the selected campaign
  useEffect(() => {
    if (!shortlistCampaign) return;
    
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const campaignRef = doc(db, 'users', user.uid, 'campaigns', shortlistCampaign);
    const unsubscribe = onSnapshot(campaignRef, (doc) => {
      if (doc.exists()) {
        const campaignData = doc.data();
        const creators = campaignData.creators || [];
        setShortlistedCreators(creators);
      } else {
        setShortlistedCreators([]);
      }
    });

    return () => unsubscribe();
  }, [shortlistCampaign]);

  // Fetch favourites on mount and when user changes
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;
    const favsRef = collection(db, 'users', user.uid, 'favourites');
    const unsubscribe = onSnapshot(favsRef, (snapshot) => {
      const favs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFavouriteCreators(favs);
    });
    return () => unsubscribe();
  }, []);

  // Define sub-filters for each tab
  const subFilters = {
    explore: [
      { key: 'all', label: 'All creators' },
      { key: 'rising', label: 'Rising Creators' },
      { key: 'pro', label: 'Pro Creators' },
      { key: 'couples', label: 'Couples' },
    ],
    recent: [], // Add if needed
    shortlist: [
      { key: 'pro', label: 'Pro Creators' },
      { key: 'couples', label: 'Couples' },
      { key: 'rising', label: 'Rising Creators' },
    ],
    favourite: [], // Add if needed
  };

  // Reset sub-filter when tab changes
  React.useEffect(() => {
    const defaultSub = subFilters[activeTab]?.[0]?.key || '';
    setActiveSubFilter(defaultSub);
  }, [activeTab]);




  // Handle removing a creator from shortlist
  const handleRemoveFromShortlist = async (creator) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const campaignRef = doc(db, 'users', user.uid, 'campaigns', shortlistCampaign);
      const campaignDoc = await getDoc(campaignRef);
      
      if (campaignDoc.exists()) {
        const campaignData = campaignDoc.data();
        const updatedCreators = (campaignData.creators || []).filter(c => c.handle !== creator.handle);
        
        await updateDoc(campaignRef, {
          creators: updatedCreators
        });
        
        setShortlistedCreators(updatedCreators);
      }
    } catch (error) {
      console.error('Error removing creator from shortlist:', error);
    }
  };

  const handleToggleFavourite = async (creator) => {
    if (!creator.id) {
      alert('This creator does not have a valid ID and cannot be shortlisted.');
      return;
    }
    // Open shortlist modal instead of direct favorite toggle
    setCreatorToShortlist(creator);
    setShowShortlistModal(true);
  };

  // Modal handlers
  const handleCreatorCardClick = (creator) => {
    setSelectedCreatorForModal(creator);
    setShowCreatorModal(true);
  };

  const handleCloseCreatorModal = () => {
    setShowCreatorModal(false);
    setSelectedCreatorForModal(null);
  };

  // Toggle selection for bulk add to campaign
  const handleToggleSelect = (creator) => {
    toggleCreatorSelection(creator);
  };

  // Open AssignCreatorModal for multiple creators
  const handleAddToCampaign = (creator) => {
    // If called from a card, add that creator to selection if not already selected
    if (creator && !isSelected(creator.id)) {
      selectCreator(creator);
    }
    setShowAssignModal(true);
  };

  return (
    <FirestoreErrorBoundary>
      <div className={`find-panel${isModal ? ' modal' : ''}`}>
      <div className="find-panel-header">
        <button onClick={onClose}>← Back</button>
        <h2>Find Creators</h2>
      </div>

      {/* Tab Navigation */}
      <div className="find-panel-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`find-tab-btn${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Campaign dropdown for Shortlist tab */}
      {activeTab === 'shortlist' && (
        <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <label htmlFor="shortlist-campaign-select" style={{ fontWeight: 500 }}>Campaign:</label>
          <select
            id="shortlist-campaign-select"
            value={shortlistCampaign}
            onChange={e => setShortlistCampaign(e.target.value)}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #eee', fontSize: 15 }}
          >
            {userCampaigns.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}


      {/* Sub-filter Pills */}
      {subFilters[activeTab] && subFilters[activeTab].length > 0 && (
        <div className="find-panel-subfilters-wrapper">
          <div className="find-panel-subfilters">
            {subFilters[activeTab].map(sub => (
              <button
                key={sub.key}
                className={`subfilter-pill${activeSubFilter === sub.key ? ' active' : ''}`}
                onClick={() => setActiveSubFilter(sub.key)}
              >
                {sub.label}
              </button>
            ))}
          </div>
          {selectedCreators.length > 0 && (
            <button
              className="add-campaign-inline-btn"
              onClick={() => setShowAssignModal(true)}
            >
              Add {selectedCreators.length} to Campaign
            </button>
          )}
        </div>
      )}

      <div className="find-panel-body">
        <CreatorFilters 
          filters={filters} 
          onFilterChange={setFilters}
          creators={creators || []}
        />
        
        
        <div className={`creator-grid-scroll-area${isModal ? ' modal' : ''}`} ref={gridScrollAreaRef}>
          <CreatorGrid
            creators={paginatedCreators}
            loading={loading}
            error={error}
            selectedCreators={selectedCreators}
            setSelectedCreators={setSelectedCreators}
            setAddToCampaignOpen={setShowAssignModal}
            activeTab={activeTab}
            shortlistCampaign={shortlistCampaign}
            onCreatorClick={handleCreatorCardClick}
            onAssignCreator={(creator) => {
              if (activeTab === 'shortlist') {
                if (!shortlistCampaign) {
                  alert('Please select a campaign first before assigning creators.');
                  return;
                }
                // Add creator to selection and open assign modal
                selectCreator(creator);
                setShowAssignModal(true);
              }
            }}
            shortlistedCreators={shortlistedCreators}
            onRemoveFromShortlist={handleRemoveFromShortlist}
            favouriteCreators={favouriteCreators}
            isFavourite={creator => favouriteCreators.some(f => f.id === creator.id)}
            onToggleFavourite={handleToggleFavourite}
            isSelected={isSelected}
            toggleCreatorSelection={toggleCreatorSelection}
          />
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="creator-pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <div className="pagination-info">
                <span>Page {currentPage} of {totalPages}</span>
              </div>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
      {showAssignModal && (
        <AssignCreatorModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            clearSelection();
          }}
          creators={selectedCreators}
          onRemoveCreator={deselectCreator}
          preselectCampaign={
            activeTab === 'shortlist' && shortlistCampaign
              ? shortlistCampaign
              : preselectCampaign
          }
          preselectBrief={preselectBrief}
          preselectScript={preselectScript}
          onAssignComplete={(assignments) => {
            console.log('Assignments completed:', assignments);
            clearSelection();
          }}
        />
      )}
      
      {/* Creator Modal */}
      {showCreatorModal && selectedCreatorForModal && (
        <CreatorModal
          creator={selectedCreatorForModal}
          onClose={handleCloseCreatorModal}
          onAddToCampaign={handleAddToCampaign}
          onToggleSelect={handleToggleSelect}
          isSelected={selectedCreators.some((c) => c.id === selectedCreatorForModal.id || c.handle === selectedCreatorForModal.handle)}
          isFavourite={favouriteCreators.some(f => f.id === selectedCreatorForModal.id)}
          onToggleFavourite={handleToggleFavourite}
        />
      )}

      {/* Shortlist Creator Modal */}
      <ShortlistCreatorModal
        isOpen={showShortlistModal}
        onClose={() => {
          setShowShortlistModal(false);
          setCreatorToShortlist(null);
        }}
        creator={creatorToShortlist}
      />
      </div>
    </FirestoreErrorBoundary>
  );
}

export default FindCreatorsPanel;
